import React, { useState, useRef } from 'react';
import { useLang } from '../../context/LangContext';
import { ScanTarget } from '@shared/types';

interface FileUploadInputProps {
  onTargetsAdd: (targets: ScanTarget[]) => void;
  disabled?: boolean;
}

interface ParsedTarget {
  target: string;
  port?: number;
  error?: string;
}

// Parse a single target entry — handles: domain, domain:port, https://domain, https://domain:port/path
function parseTargetEntry(raw: string): { hostname: string; port: number } | null {
  let s = raw.trim();
  if (!s || s.startsWith('#')) return null;
  s = s.replace(/^["']|["']$/g, '');
  s = s.replace(/^htpps?:\/\//i, 'https://');
  if (/^https?:\/\//i.test(s)) {
    try { const u = new URL(s); return { hostname: u.hostname, port: u.port ? parseInt(u.port) : 443 }; } catch {}
  }
  if (s.includes('://')) return null;
  try { const u = new URL('https://' + s); return { hostname: u.hostname, port: u.port ? parseInt(u.port) : 443 }; } catch {}
  return { hostname: s, port: 443 };
}

export const FileUploadInput: React.FC<FileUploadInputProps> = ({ onTargetsAdd, disabled = false }) => {
  const { lang } = useLang();
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState<ParsedTarget[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const supportedFormats = ['.csv', '.txt'];
  const maxFileSize = 10 * 1024 * 1024;

  const parseCSV = (content: string): ParsedTarget[] => {
    const targets: ParsedTarget[] = [];
    const lines = content.split('\n');
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;
      // Skip header row
      if (index === 0 && /^(target|host|domain|url|site)/i.test(trimmed)) return;
      const parts = trimmed.split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
      const parsed = parseTargetEntry(parts[0]);
      if (!parsed) { targets.push({ target: parts[0] || '', error: `Line ${index + 1}: Invalid` }); return; }
      const port = parts[1] ? parseInt(parts[1]) : parsed.port;
      if (isNaN(port) || port < 1 || port > 65535) { targets.push({ target: parsed.hostname, error: `Line ${index + 1}: Bad port` }); return; }
      targets.push({ target: parsed.hostname, port });
    });
    return targets;
  };

  const parseTXT = (content: string): ParsedTarget[] => {
    const targets: ParsedTarget[] = [];
    const lines = content.split('\n');
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;
      // Skip header row
      if (index === 0 && /^(target|host|domain|url|site)/i.test(trimmed)) return;
      const parsed = parseTargetEntry(trimmed);
      if (!parsed) { targets.push({ target: trimmed, error: `Line ${index + 1}: Invalid` }); return; }
      targets.push({ target: parsed.hostname, port: parsed.port });
    });
    return targets;
  };

  const validateTarget = (t: string) => {
    if (/^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/.test(t)) return { isValid: true, type: 'ip' as const };
    // Only treat as range if it's IP CIDR (192.168.1.0/24) or IP range (192.168.1.1-192.168.1.254)
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/.test(t)) return { isValid: true, type: 'range' as const };
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}-\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(t)) return { isValid: true, type: 'range' as const };
    // Accept any hostname that contains a dot (domain)
    if (t.includes('.')) return { isValid: true, type: 'domain' as const };
    if (/^[a-zA-Z0-9][a-zA-Z0-9&_-]*$/.test(t)) return { isValid: true, type: 'domain' as const };
    return { isValid: false, type: 'ip' as const };
  };

  const processFile = async (f: File) => {
    setIsProcessing(true); setError('');
    try {
      const content = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target?.result as string); r.onerror = () => rej(new Error('Read failed')); r.readAsText(f); });
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      let parsed: ParsedTarget[];
      if (ext === '.csv') parsed = parseCSV(content);
      else if (ext === '.txt') parsed = parseTXT(content);
      else { setError('Unsupported format'); return; }
      parsed.forEach(p => { if (!p.error && p.target && !validateTarget(p.target).isValid) p.error = 'Invalid format'; });
      // Sort: errors first so they're visible in preview
      parsed.sort((a, b) => (a.error ? 0 : 1) - (b.error ? 0 : 1));
      setPreviewData(parsed); setShowPreview(true);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
    finally { setIsProcessing(false); }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > maxFileSize) { setError('File > 10MB'); return; }
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!supportedFormats.includes(ext)) { setError('Use .csv or .txt'); return; }
    setFile(f); setError(''); setShowPreview(false); processFile(f);
  };

  const handleConfirm = () => {
    const valid = previewData.filter(p => !p.error && p.target);
    onTargetsAdd(valid.map(p => ({ type: validateTarget(p.target).type, value: p.target, port: p.port || 443 })));
    setFile(null); setPreviewData([]); setShowPreview(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCancel = () => { setFile(null); setPreviewData([]); setShowPreview(false); setError(''); if (fileInputRef.current) fileInputRef.current.value = ''; };
  const validCount = previewData.filter(p => !p.error && p.target).length;
  const errorCount = previewData.filter(p => p.error || !p.target).length;

  return (
    <div className="file-upload-input">
      <h3>{lang === 'zh' ? '批次掃描' : 'Batch Scan'}</h3>
      <div className="upload-section">
        <div className="form-group">
          <label htmlFor="file-input">{lang === 'zh' ? '選擇檔案：' : 'Select File:'}</label>
          <input ref={fileInputRef} id="file-input" type="file" accept=".csv,.txt" onChange={handleFileChange} disabled={disabled || isProcessing} />
          <small className="help-text">
            {lang === 'zh' ? '支援格式' : 'Supported'}: .csv, .txt (max 10MB)<br />
            CSV: target,port (e.g., example.com,443 or https://example.com)<br />
            TXT: one per line (e.g., example.com:443 or https://example.com)
          </small>
        </div>
        {error && <div className="error-message">{error}</div>}
        {isProcessing && <div className="processing-indicator">Processing...</div>}
      </div>
      {showPreview && previewData.length > 0 && (
        <div className="preview-section">
          <h4>{lang === 'zh' ? '檔案預覽' : 'File Preview'}</h4>
          <div className="preview-stats">
            <span className="valid-count">{lang === 'zh' ? '有效' : 'Valid'}: {validCount}</span>
            {errorCount > 0 && <span className="error-count">{lang === 'zh' ? '錯誤' : 'Errors'}: {errorCount}</span>}
          </div>
          <div className="preview-table">
            <table>
              <thead><tr><th>Target</th><th>Port</th><th>Status</th></tr></thead>
              <tbody>
                {previewData.slice(0, Math.max(10, errorCount + 3)).map((p, i) => (
                  <tr key={i} className={p.error ? 'error-row' : 'valid-row'}>
                    <td>{p.target || 'N/A'}</td><td>{p.port || 'N/A'}</td><td>{p.error || 'Valid'}</td>
                  </tr>
                ))}
                {previewData.length > Math.max(10, errorCount + 3) && <tr><td colSpan={3}>... +{previewData.length - Math.max(10, errorCount + 3)} more</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="preview-actions">
            <button onClick={handleConfirm} disabled={validCount === 0} className="confirm-btn">
              {lang === 'zh' ? `匯入 ${validCount} 個目標` : `Import ${validCount} Targets`}
            </button>
            <button onClick={handleCancel} className="cancel-btn">{lang === 'zh' ? '取消' : 'Cancel'}</button>
          </div>
        </div>
      )}
    </div>
  );
};
